import type {
  SettingAccess,
  SettingAgentEditability,
  SettingAgentEditabilityMode,
  SettingChangeGuidance,
  SettingDefinition,
  SettingDependency,
  SettingGroupChatSurface,
  SettingGroupDefinition,
  SettingGroupFullPageTarget,
  SettingMetadata,
  SettingOwner,
  SettingProductArea,
  SettingReadAccess,
  SettingReadValueVisibility,
  SettingRisk,
  SettingRiskLevel,
  SettingSourceKind,
  SettingValueAdapterId,
  SettingValueSemantics,
  SettingValueSource,
  SettingVisibility,
  SettingWriteGrant,
} from "./types";

const SettingVisibilityValues = {
  merchant: true,
  admin: true,
  restricted: true,
} as const satisfies Record<SettingVisibility, true>;

const SettingProductAreaValues = {
  support: true,
  ai: true,
  "customer-widget": true,
  integrations: true,
  returns: true,
  fulfillment: true,
  billing: true,
  general: true,
} as const satisfies Record<SettingProductArea, true>;

const SettingReadValueVisibilityValues = {
  full: true,
  redacted: true,
  presence: true,
} as const satisfies Record<SettingReadValueVisibility, true>;

const SettingRiskLevelValues = {
  low: true,
  medium: true,
  high: true,
  critical: true,
} as const satisfies Record<SettingRiskLevel, true>;

const SettingSourceKindValues = {
  teamSettings: true,
  crdb: true,
  accountBilling: true,
  integration: true,
  provider: true,
  computed: true,
  workflow: true,
} as const satisfies Record<SettingSourceKind, true>;

const SettingValueAdapterIdValues = {
  teamSettings: true,
  crdb: true,
  accountBilling: true,
  integration: true,
  provider: true,
  computed: true,
  workflow: true,
} as const satisfies Record<SettingValueAdapterId, true>;

const SettingWriteGrantMethodValues = {
  direct: true,
  workflow: true,
  provider: true,
} as const satisfies Record<SettingWriteGrant["method"], true>;

const SettingAgentEditabilityModeValues = {
  safeToApply: true,
  requiresApproval: true,
  suggestOnly: true,
  never: true,
} as const satisfies Record<SettingAgentEditabilityMode, true>;

const SettingDependencyRelationshipValues = {
  requires: true,
  controls: true,
  controlledBy: true,
  overrides: true,
  related: true,
  computedFrom: true,
} as const satisfies Record<SettingDependency["relationship"], true>;

const SettingGroupFullPageTargetTypeValues = {
  settings: true,
  legacySupport: true,
  settingsHome: true,
} as const satisfies Record<SettingGroupFullPageTarget["type"], true>;

const SettingGroupChatSurfaceTypeValues = {
  inline: true,
  fullPage: true,
} as const satisfies Record<SettingGroupChatSurface["type"], true>;

const SettingValueInputKindValues = {
  color: true,
  email: true,
  textarea: true,
  timezone: true,
  url: true,
} as const satisfies Record<
  NonNullable<SettingValueSemantics["inputKind"]>,
  true
>;

const SettingValueSelectionModeValues = {
  single: true,
  multiple: true,
} as const satisfies Record<
  NonNullable<SettingValueSemantics["selectionMode"]>,
  true
>;

export function validateSettingsProvider(
  providerName: string,
  groups: readonly SettingGroupDefinition[],
  definitions: readonly SettingDefinition[],
  groupById: Readonly<Record<string, SettingGroupDefinition>>,
): void {
  for (const group of groups) {
    validateSettingGroup(providerName, group);
  }

  for (const definition of definitions) {
    if (!groupById[definition.groupId]) {
      throw new Error(
        `${providerName} setting ${definition.id} references unknown group ${definition.groupId}`,
      );
    }
    validateSettingDefinition(providerName, definition, groupById);
  }
}

function validateSettingGroup(
  providerName: string,
  group: SettingGroupDefinition,
): void {
  const kind = `${providerName} setting group ${group.id}`;
  validateRequiredText(kind, "id", group.id);
  validateRequiredText(kind, "title", group.title);
  validateRequiredText(kind, "description", group.description);
  validateEnumValue(
    kind,
    "productArea",
    group.productArea,
    SettingProductAreaValues,
  );
  validateSettingAccess(kind, group.access);
  validateSettingOwner(kind, group.owner);
  validateSettingRisk(kind, group.risk);
  validateAgentEditability(kind, group.agentEditability);
  validateTextList(kind, "synonyms", group.synonyms);
  validateValueSemantics(kind, group.valueSemantics);
  validateChangeGuidance(kind, group.changeGuidance);
  validateSettingDependencies(kind, group.dependencies);
  validateFullPageTarget(kind, group.fullPageTarget);
  validateChatSurface(kind, group.chatSurface);
}

function validateSettingDefinition(
  providerName: string,
  definition: SettingDefinition,
  groupById: Readonly<Record<string, SettingGroupDefinition>>,
): void {
  const kind = `${providerName} setting ${definition.id}`;
  validateRequiredText(kind, "id", definition.id);
  validateSettingMetadata(kind, definition.metadata);
  validateValueSource(kind, definition.valueSource);

  for (const groupId of definition.metadata.relatedGroups ?? []) {
    if (!groupById[groupId]) {
      throw new Error(`${kind} references unknown related group ${groupId}`);
    }
  }
}

function validateSettingMetadata(
  kind: string,
  metadata: SettingMetadata | undefined,
): void {
  if (!metadata) {
    throw new Error(`${kind} is missing metadata`);
  }

  validateRequiredText(kind, "metadata.label", metadata.label);
  validateRequiredText(kind, "metadata.description", metadata.description);
  validateEnumValue(
    kind,
    "metadata.productArea",
    metadata.productArea,
    SettingProductAreaValues,
  );
  validateSettingAccess(kind, metadata.access);
  validateSettingOwner(kind, metadata.owner);
  validateSettingRisk(kind, metadata.risk);
  validateAgentEditability(kind, metadata.agentEditability);
  validateTextList(kind, "metadata.relatedGroups", metadata.relatedGroups);
  validateTextList(kind, "metadata.synonyms", metadata.synonyms);
  validateValueSemantics(kind, metadata.valueSemantics);
  validateChangeGuidance(kind, metadata.changeGuidance);
  validateSettingDependencies(kind, metadata.dependencies);
}

function validateSettingAccess(
  kind: string,
  access: SettingAccess | undefined,
): void {
  if (!access) {
    throw new Error(`${kind} is missing access`);
  }

  validateReadAccess(kind, access.read);

  if (!access.write) {
    throw new Error(`${kind} is missing write access`);
  }

  switch (access.write.mode) {
    case "none":
      validateRequiredText(kind, "access.write.reason", access.write.reason);
      return;
    case "grants":
      if (
        !Array.isArray(access.write.grants) ||
        access.write.grants.length === 0
      ) {
        throw new Error(`${kind} access.write grants must not be empty`);
      }
      for (const [index, grant] of access.write.grants.entries()) {
        validateWriteGrant(`${kind} access.write grant ${index + 1}`, grant);
      }
      return;
    default:
      throw new Error(`${kind} has invalid access.write.mode`);
  }
}

function validateReadAccess(
  kind: string,
  readAccess: SettingReadAccess | undefined,
): void {
  if (!readAccess) {
    throw new Error(`${kind} is missing read access`);
  }

  validateEnumValue(
    kind,
    "access.read.minVisibility",
    readAccess.minVisibility,
    SettingVisibilityValues,
  );

  if (readAccess.valueVisibility !== undefined) {
    validateEnumValue(
      kind,
      "access.read.valueVisibility",
      readAccess.valueVisibility,
      SettingReadValueVisibilityValues,
    );
  }
}

function validateWriteGrant(kind: string, grant: SettingWriteGrant): void {
  validateEnumValue(
    kind,
    "method",
    grant.method,
    SettingWriteGrantMethodValues,
  );
  validateEnumValue(
    kind,
    "minVisibility",
    grant.minVisibility,
    SettingVisibilityValues,
  );

  if (grant.permission !== undefined) {
    validateRequiredText(kind, "permission", grant.permission);
  }
  if (grant.note !== undefined) {
    validateRequiredText(kind, "note", grant.note);
  }

  switch (grant.method) {
    case "direct":
      if (
        hasOwnField(grant, "workflowId") ||
        hasOwnField(grant, "providerId")
      ) {
        throw new Error(
          `${kind} direct write grant must not include workflowId or providerId`,
        );
      }
      return;
    case "workflow":
      validateRequiredText(kind, "workflowId", grant.workflowId);
      if (hasOwnField(grant, "providerId")) {
        throw new Error(
          `${kind} workflow write grant must not include providerId`,
        );
      }
      return;
    case "provider":
      validateRequiredText(kind, "providerId", grant.providerId);
      if (hasOwnField(grant, "workflowId")) {
        throw new Error(
          `${kind} provider write grant must not include workflowId`,
        );
      }
      return;
    default:
      throw new Error(`${kind} has invalid method`);
  }
}

function validateSettingOwner(
  kind: string,
  owner: SettingOwner | undefined,
): void {
  if (!owner) {
    throw new Error(`${kind} is missing owner`);
  }

  validateEnumValue(kind, "owner.kind", owner.kind, SettingSourceKindValues);
  if (owner.path !== undefined) {
    validateRequiredText(kind, "owner.path", owner.path);
  }
  if (owner.product !== undefined) {
    validateRequiredText(kind, "owner.product", owner.product);
  }
}

function validateSettingRisk(
  kind: string,
  risk: SettingRisk | undefined,
): void {
  if (!risk) {
    throw new Error(`${kind} is missing risk`);
  }

  validateEnumValue(kind, "risk.level", risk.level, SettingRiskLevelValues);
  if (risk.reason !== undefined) {
    validateRequiredText(kind, "risk.reason", risk.reason);
  }
}

function validateAgentEditability(
  kind: string,
  agentEditability: SettingAgentEditability | undefined,
): void {
  if (!agentEditability) {
    throw new Error(`${kind} is missing agent editability`);
  }

  validateEnumValue(
    kind,
    "agentEditability.mode",
    agentEditability.mode,
    SettingAgentEditabilityModeValues,
  );
  if (agentEditability.reason !== undefined) {
    validateRequiredText(
      kind,
      "agentEditability.reason",
      agentEditability.reason,
    );
  }
}

function validateSettingDependencies(
  kind: string,
  dependencies: readonly SettingDependency[] | undefined,
): void {
  if (dependencies !== undefined && !Array.isArray(dependencies)) {
    throw new Error(`${kind} dependencies must be an array`);
  }

  if (dependencies) {
    for (const [index, dependency] of dependencies.entries()) {
      const dependencyKind = `${kind} dependency ${index + 1}`;
      if (!dependency.key && !dependency.group) {
        throw new Error(`${dependencyKind} must target a key or group`);
      }
      if (dependency.key !== undefined) {
        validateRequiredText(dependencyKind, "key", dependency.key);
      }
      if (dependency.group !== undefined) {
        validateRequiredText(dependencyKind, "group", dependency.group);
      }
      validateEnumValue(
        dependencyKind,
        "relationship",
        dependency.relationship,
        SettingDependencyRelationshipValues,
      );
      if (dependency.description !== undefined) {
        validateRequiredText(
          dependencyKind,
          "description",
          dependency.description,
        );
      }
    }
  }
}

function validateValueSemantics(
  kind: string,
  valueSemantics: SettingValueSemantics | undefined,
): void {
  if (!valueSemantics) {
    return;
  }

  if (valueSemantics.summary !== undefined) {
    validateRequiredText(
      kind,
      "valueSemantics.summary",
      valueSemantics.summary,
    );
  }
  if (valueSemantics.trueLabel !== undefined) {
    validateRequiredText(
      kind,
      "valueSemantics.trueLabel",
      valueSemantics.trueLabel,
    );
  }
  if (valueSemantics.falseLabel !== undefined) {
    validateRequiredText(
      kind,
      "valueSemantics.falseLabel",
      valueSemantics.falseLabel,
    );
  }
  if (valueSemantics.unsetLabel !== undefined) {
    validateRequiredText(
      kind,
      "valueSemantics.unsetLabel",
      valueSemantics.unsetLabel,
    );
  }
  if (valueSemantics.inputKind !== undefined) {
    validateEnumValue(
      kind,
      "valueSemantics.inputKind",
      valueSemantics.inputKind,
      SettingValueInputKindValues,
    );
  }
  if (valueSemantics.unit !== undefined) {
    validateRequiredText(kind, "valueSemantics.unit", valueSemantics.unit);
  }
  if (valueSemantics.selectionMode !== undefined) {
    validateEnumValue(
      kind,
      "valueSemantics.selectionMode",
      valueSemantics.selectionMode,
      SettingValueSelectionModeValues,
    );
  }
  if (valueSemantics.options !== undefined) {
    if (!Array.isArray(valueSemantics.options)) {
      throw new Error(`${kind} valueSemantics.options must be an array`);
    }

    for (const [index, option] of valueSemantics.options.entries()) {
      const optionKind = `${kind} valueSemantics option ${index + 1}`;
      validateRequiredText(optionKind, "value", option.value);
      validateRequiredText(optionKind, "label", option.label);
      if (option.description !== undefined) {
        validateRequiredText(optionKind, "description", option.description);
      }
    }
  }
}

function validateChangeGuidance(
  kind: string,
  changeGuidance: SettingChangeGuidance | undefined,
): void {
  if (!changeGuidance) {
    return;
  }

  if (changeGuidance.summary !== undefined) {
    validateRequiredText(
      kind,
      "changeGuidance.summary",
      changeGuidance.summary,
    );
  }
  validateTextList(
    kind,
    "changeGuidance.askBeforeChanging",
    changeGuidance.askBeforeChanging,
  );
  if (changeGuidance.confirmation !== undefined) {
    validateRequiredText(
      kind,
      "changeGuidance.confirmation",
      changeGuidance.confirmation,
    );
  }
  if (changeGuidance.warning !== undefined) {
    validateRequiredText(
      kind,
      "changeGuidance.warning",
      changeGuidance.warning,
    );
  }
}

function validateFullPageTarget(
  kind: string,
  fullPageTarget: SettingGroupFullPageTarget | undefined,
): void {
  if (!fullPageTarget) {
    return;
  }

  validateEnumValue(
    kind,
    "fullPageTarget.type",
    fullPageTarget.type,
    SettingGroupFullPageTargetTypeValues,
  );

  switch (fullPageTarget.type) {
    case "settings":
    case "legacySupport":
      validateRequiredText(kind, "fullPageTarget.path", fullPageTarget.path);
      return;
    case "settingsHome":
      if (hasOwnField(fullPageTarget, "path")) {
        throw new Error(`${kind} settingsHome target must not include a path`);
      }
      return;
    default:
      throw new Error(`${kind} has invalid fullPageTarget.type`);
  }
}

function validateChatSurface(
  kind: string,
  chatSurface: SettingGroupChatSurface | undefined,
): void {
  if (!chatSurface) {
    return;
  }

  validateEnumValue(
    kind,
    "chatSurface.type",
    chatSurface.type,
    SettingGroupChatSurfaceTypeValues,
  );

  if (chatSurface.type === "fullPage" && chatSurface.reason !== undefined) {
    validateRequiredText(kind, "chatSurface.reason", chatSurface.reason);
    return;
  }

  if (chatSurface.type === "inline" && hasOwnField(chatSurface, "reason")) {
    throw new Error(`${kind} inline chat surface must not include a reason`);
  }
}

function validateValueSource(
  kind: string,
  valueSource: SettingValueSource | undefined,
): void {
  if (!valueSource) {
    throw new Error(`${kind} is missing value source`);
  }

  validateEnumValue(
    kind,
    "valueSource.adapter",
    valueSource.adapter,
    SettingValueAdapterIdValues,
  );

  if (valueSource.adapter === "teamSettings") {
    validateRequiredText(kind, "valueSource.key", valueSource.key);
    if (hasOwnField(valueSource, "ref")) {
      throw new Error(`${kind} teamSettings value source must not include ref`);
    }
    return;
  }

  validateRequiredText(kind, "valueSource.ref", valueSource.ref);
  if (hasOwnField(valueSource, "key")) {
    throw new Error(`${kind} external value source must not include key`);
  }
}

function validateTextList(
  kind: string,
  fieldName: string,
  values: readonly string[] | undefined,
): void {
  if (values === undefined) {
    return;
  }

  if (!Array.isArray(values)) {
    throw new Error(`${kind} ${fieldName} must be an array`);
  }

  for (const [index, value] of values.entries()) {
    validateRequiredText(kind, `${fieldName}[${index}]`, value);
  }
}

function validateRequiredText(
  kind: string,
  fieldName: string,
  value: unknown,
): void {
  if (!isNonEmptyString(value)) {
    throw new Error(`${kind} has invalid ${fieldName}`);
  }
}

function validateEnumValue<T extends string>(
  kind: string,
  fieldName: string,
  value: unknown,
  allowedValues: Record<T, true>,
): void {
  if (!isNonEmptyString(value) || !hasOwnField(allowedValues, value)) {
    throw new Error(`${kind} has invalid ${fieldName}`);
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function hasOwnField(object: object, field: string): boolean {
  return Object.prototype.hasOwnProperty.call(object, field);
}
